import { CSSProperties } from "react";
import styled, { createGlobalStyle, AnyStyledComponent, css } from "styled-components";
import { BarChart, LineChart, barChartClassNames, lineChartClassNames } from "@evergis/charts";
import { Icon, IconButton, transition } from "@evergis/ui";

import { LegendProps } from "./types";

export const BarChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

export const BarChartContainer = styled.div`
  width: 100%;
  height: 250px;
`;

export const StyledBarChart = styled(BarChart)`
  .${barChartClassNames.barChartYAxis}, .${barChartClassNames.barChartXAxis} {
    .domain,
    .tick line {
      visibility: hidden;
    }
  }

  .${barChartClassNames.barChartGridLineX} {
    stroke: ${({ theme: { palette } }) => palette.element};
  }

  text {
    fill: ${({ theme: { palette } }) => palette.textDisabled};
  }
`;

export const StyledLineChart = styled(LineChart)`
  .${lineChartClassNames.lineChartYScaleGlobal}, .${lineChartClassNames.lineChartXScaleGlobal} {
    .domain {
      visibility: hidden;
    }

    .tick {
      text {
        fill: ${({ theme: { palette } }) => palette.textDisabled};
      }

      line {
        visibility: hidden;
      }
    }
  }

  .${lineChartClassNames.lineChartXScaleGlobal} {
    .tick {
      :first-of-type {
        text {
          text-anchor: start;
        }
      }

      :last-of-type {
        text {
          text-anchor: end;
        }
      }
    }
  }

  .${lineChartClassNames.lineChartLine} {
    stroke-width: 2px;
  }

  .${lineChartClassNames.lineChartLabel} {
    color: ${({ theme: { palette } }) => palette.textPrimary};
  }

  .${lineChartClassNames.lineChartMouseCircle} {
    stroke: #ffffff;
    stroke-width: 2px;
  }

  .${lineChartClassNames.lineChartGridLineX} {
    stroke: ${({ theme: { palette } }) => palette.element};
  }

  text {
    fill: ${({ theme: { palette } }) => palette.textDisabled};
  }
`;

export const PieChartWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 2.5rem;
`;

export const PieChartContainer = styled.div`
  width: auto;
`;

export const PieChartLegendContainer = styled.div`
  flex-grow: 1;
  width: calc(100% - 10rem - 2.5rem);
  box-sizing: border-box;
`;

export const PieChartIcon = styled.div`
  text-align: center;
  color: #18191a;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.3;

  & > span::after {
    font-size: 24px;
    color: ${({ theme: { palette } }) => palette.textDisabled};
  }
`;

export const LegendWrapper = styled.div<Pick<LegendProps, "toggleAlign">>`
  display: flex;
  flex-direction: column;
  align-items: ${({ toggleAlign }) => toggleAlign};
  gap: 0.5rem;
`;

export const LegendContainer = styled.div<Omit<LegendProps, "items" | "toggleAlign">>`
  display: flex;
  justify-content: ${({ justify }) => justify ?? "initial"};
  flex-direction: ${({ direction }) => direction ?? "column"};
  flex-wrap: ${({ wrap }) => wrap ?? "wrap"};
  gap: ${({ gap }) => gap ?? "0.25rem"};
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
  font-size: 0.625rem;
  line-height: 1.2;
`;

export const ColorBox = styled.div<{ color?: CSSProperties["backgroundColor"] }>`
  flex-shrink: 0;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 0.125rem;
  background-color: ${({ color }) => color};
`;

export const Tooltip = styled.div<{ transform?: CSSProperties["transform"] }>`
  position: relative;
  border-radius: 0.25rem;
  background-color: rgba(28, 33, 48);
  padding: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem 0 rgba(0, 0, 0, 0.15);
  font-size: 0.75rem;
  transform: ${({ transform }) => transform};
  opacity: 0.75;

  :before {
    position: absolute;
    display: block;
    content: "";
    width: 0;
    height: 0;
  }
`;

export const TooltipTitle = styled.div`
  font-weight: 500;
  margin-bottom: 6px;
  color: #ffffff;
`;

export const TooltipFlex = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem;
  color: #ffffff;
`;

export const TooltipTip = styled.div`
  position: absolute;
  width: 0.5rem;
  height: 0.5rem;
  left: 50%;
  bottom: 0.25rem;
  transform: translate(-50%, 100%) rotate(45deg);
  background-color: rgba(28, 33, 48);
`;

export const LineChartTooltipStyles = createGlobalStyle`
  .lineChartTooltip.${lineChartClassNames.lineChartMouseLabel} {
    .${lineChartClassNames.lineChartLabelFlex} {
      justify-content: center;
      align-items: flex-end;
          
      ${Tooltip} {
        margin: 0 0 8px 0;
            
        :before {
          top: auto;
          bottom: 0;
          left: 50%;
          transform: translate(-50%, 100%);
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-top: 4px solid rgba(28, 33, 48, 0.9);
        }
      }
    }
  }
`;

export const SimpleButton = styled(IconButton as AnyStyledComponent)<{
  toggled?: boolean;
  hoverIcon?: boolean;
}>`
  margin: 0;
  padding: 0;
  height: 0.75rem;
  cursor: pointer;
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-end;

  ${({ hoverIcon }) =>
    hoverIcon &&
    css`
      ${Icon} {
        opacity: 0;
        transition: opacity ${transition.hover};
      }

      &:hover {
        ${Icon} {
          opacity: 1;
        }
      }
    `}

  &:hover,
  &:active {
    ${Icon}, ${Icon} + span {
      color: ${({ theme: { palette } }) => palette.primaryDeep};
      transform: scale(1);
    }
  }

  ${Icon} + span {
    line-height: 0;
    font-size: 0.75rem;
    color: ${({ theme: { palette } }) => palette.primary};
    margin: 0 0.25rem 0 0;
  }

  ${Icon}, ${Icon} + span {
    align-items: center;
    color: ${({ theme: { palette } }) => palette.primary};
    display: flex;
    text-align: left;
    transition: color ${transition.hover};

    &:after {
      margin-left: 0.125rem;
      position: static;
      transform: ${({ toggled }) => `rotate(${toggled ? 180 : 0}deg)`};
      transition: transform ${transition.hover};
    }
  }

  ${({ kind }) =>
    !kind &&
    css`
      && ${Icon} {
        display: none;
      }
    `}
`;

SimpleButton.defaultProps = {
  kind: "expand",
};
